import { useEffect, useRef, type MutableRefObject } from 'react';
import { useThree } from '@react-three/fiber';
import type * as THREE from 'three';
import {
  ensureDecalAtlasLoaded,
  getDecalAtlasTextures,
} from '../../materials/core/DamageDecalAtlas';
import type { DemolitionRuntime } from '../../structures/demolition/demolitionPhysics';
import type { DemolitionFxController } from '../fallback/ImpactFx';
import type { PowderResidueController } from './PowderResidueLayer';
import {
  buildFirstHitWarmupGroup,
  disposeFirstHitWarmupGroup,
  runFirstHitPrewarm,
  warmFirstHitCpuPaths,
  type FirstHitPrewarmStats,
} from './firstHitPrewarm';

const READY_FALLBACK_MS = 4000;

export function FirstHitPrewarm({
  liveMaterials,
  runtime,
  residueRef,
  fxRef,
  onDone,
}: {
  liveMaterials: THREE.Material[];
  runtime: DemolitionRuntime;
  residueRef: MutableRefObject<PowderResidueController | null>;
  fxRef: MutableRefObject<DemolitionFxController | null>;
  onDone: (stats: FirstHitPrewarmStats) => void;
}) {
  const { gl, scene, camera } = useThree();
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    let cancelled = false;
    let finished = false;
    const rafIds: number[] = [];
    const t0 = performance.now();

    const finish = (stats: FirstHitPrewarmStats) => {
      if (cancelled || finished) return;
      finished = true;
      if (import.meta.env.DEV) {
        (window as Window & { __firstHitPrewarm?: FirstHitPrewarmStats }).__firstHitPrewarm = stats;
      }
      onDoneRef.current(stats);
    };

    const fallback = window.setTimeout(() => {
      finish({
        compileMs: 0,
        textureMs: 0,
        cpuMs: 0,
        totalMs: performance.now() - t0,
        meshCount: 0,
        materialCount: 0,
      });
    }, READY_FALLBACK_MS);

    const built = buildFirstHitWarmupGroup(liveMaterials, []);
    scene.add(built.group);

    const afterDrawn = () => {
      void (async () => {
        const textureT0 = performance.now();
        try {
          await ensureDecalAtlasLoaded();
        } catch {
          // Atlas is best-effort.
        }
        const atlases = getDecalAtlasTextures();
        if (atlases.concrete) gl.initTexture(atlases.concrete);
        if (atlases.brick) gl.initTexture(atlases.brick);
        if (atlases.stone) gl.initTexture(atlases.stone);
        if (atlases.wood) gl.initTexture(atlases.wood);
        const textureMs = performance.now() - textureT0;
        const cpuT0 = performance.now();
        warmFirstHitCpuPaths({
          cell: runtime.pristineCells[0] ?? runtime.cells[0] ?? null,
          residue: residueRef.current,
          fx: fxRef.current,
          wallHalf: runtime.wallHalf,
        });
        const cpuMs = performance.now() - cpuT0;
        disposeFirstHitWarmupGroup(built);
        window.clearTimeout(fallback);
        finish({
          compileMs: performance.now() - t0 - textureMs - cpuMs,
          textureMs,
          cpuMs,
          totalMs: performance.now() - t0,
          meshCount: 0,
          materialCount: liveMaterials.length,
        });
        window.setTimeout(() => {
          if (cancelled) return;
          void runFirstHitPrewarm({
            renderer: gl,
            scene,
            camera,
            liveMaterials: [],
            profileIds: (['brick', 'wood', 'stone', 'glass'] as const).filter(
              (id) => id !== runtime.profile.id,
            ),
          }).catch(() => undefined);
        }, 30000);
      })();
    };

    // Two rAFs so R3F actually draws the warmup meshes (compile-on-first-draw)
    // instead of renderer.compile() walking the whole scene and freezing e2e.
    rafIds.push(
      window.requestAnimationFrame(() => {
        rafIds.push(window.requestAnimationFrame(afterDrawn));
      }),
    );

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      for (const id of rafIds) window.cancelAnimationFrame(id);
      disposeFirstHitWarmupGroup(built);
    };
    // One-shot: R3F gl/camera/scene identities can change on the first frames.
    // Re-running would trip `started` and leave canvasReady false forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
